import { useCallback, useEffect, useState, useRef, useMemo } from "react";
import api from "api/api";
import { AgGridReact } from "ag-grid-react";
import Select from "react-select";

// mui components
import Button from "@mui/material/Button";
import ControlPointOutlinedIcon from "@mui/icons-material/ControlPointOutlined";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";

import Loader from "../../common/Loader";

import { useSelector, useDispatch } from "react-redux";
import { showNotification } from "store/features/notificationSlice";
import { loadRespondentSettingsStudyType, loadRespondentSettingsStudyTypeMethod } from "store/features/settingsSlice";
import EditCell from "./EditCell";
import SettingsAddUpdate from "./SettingsAddUpdate";
import RespondentSettingsData from "../../../utils/RespondentSettingsData";
import respondentSettingsUtils from "../../../utils/respondentSettingsUtils";

import {
    transformStudyTypeOptions,
    transformStudyTypeMethodOptions,
    formatStudyTypeLabel,
    formatMethodLabel,
    getStudyTypeMethodSelectStyles,
    getInfoMessageOption
} from "../../../utils/studyTypeUtils";

// Styles
import ReactSelectStyles from "../../../styles/ReactSelectStyles";

function Settings() {
    const gridRef = useRef();
    const dispatch = useDispatch();

    const productCategoryData = useSelector((state) => state.settingsStore.productCategory);
    const productCategoryId = productCategoryData?.value || null;
    const productCategoryLabel = productCategoryData?.label || null;

    const [rowData, setRowData] = useState([]);

    const [selectedRows, setSelectedRows] = useState([]);
    const [selectedRowToUpdate, setSelectedRowToUpdate] = useState([]);

    const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
    const [isUpdateDialogOpen, setIsUpdateDialogOpen] = useState(false);
    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    const [studyTypeOptions, setStudyTypeOptions] = useState([]);
    const [studyTypeMethodOptions, setStudyTypeMethodOptions] = useState([]);
    const [selectedStudyType, setSelectedStudyType] = useState(null);
    const [selectedStudyTypeMethod, setSelectedStudyTypeMethod] = useState([]);

    const [uniqueBackendVariableList, setUniqueBackendVariableList] = useState([]);
    useEffect(() => {
        return () => {
            dispatch(loadRespondentSettingsStudyType(null));
            dispatch(loadRespondentSettingsStudyTypeMethod(null));
        };
    }, [dispatch]);

    const optionsWithInfoMessage = useMemo(() => {
        if (studyTypeMethodOptions.length === 0) return [];
        const infoMessage = getInfoMessageOption();
        return [infoMessage, ...studyTypeMethodOptions];
    }, [studyTypeMethodOptions]);

    const customMethodReactSelectStyles = useMemo(
        () => getStudyTypeMethodSelectStyles(ReactSelectStyles),
        []
    );

    const getTypesOfVariableForCell = useCallback(() => {
        return respondentSettingsUtils(productCategoryLabel);
    }, [productCategoryLabel]);

    const editCellRenderer = (row) => {
        return (
            <EditCell
                row={row}
                onEdit={handleUpdateClick}
            />
        );
    };

    const columnDefs = [
        {
            headerName: "Backend Variable",
            field: "backend_variable",
            minWidth: 320,
            checkboxSelection: true,
            headerCheckboxSelection: true,
            autoHeight: true,
            filter: true,
            suppressMenu: true,
            floatingFilter: true,
            filterParams: {
                filterOptions: ["contains"],
            },
        },
        {
            headerName: "Display Label",
            field: "display_label",
            minWidth: 320,
            autoHeight: true,
            filter: true,
            suppressMenu: true,
            floatingFilter: true,
            filterParams: {
                filterOptions: ["contains"],
            },
        },
        {
            headerName: "Variable Category",
            field: "variable_category",
            minWidth: 320,
            autoHeight: true,
            filter: true,
            suppressMenu: true,
            floatingFilter: true,
            filterParams: {
                filterOptions: ["contains"],
            },
        },
        {
            headerName: "Type of Variable",
            field: "type_of_variable",
            minWidth: 200,
            autoHeight: true,
            filter: true,
            suppressMenu: true,
            floatingFilter: true,
            filterParams: {
                filterOptions: ["contains"],
            },
            cellRenderer: (row) => {
                const options = getTypesOfVariableForCell();
                return options.filter((ele) => ele.value === row.value)?.[0]?.label || row.value;
            },
        },
        {
            headerName: "Type of Fixed Graph",
            field: "type_of_graph",
            minWidth: 200,
            autoHeight: true,
            filter: true,
            suppressMenu: true,
            floatingFilter: true,
            filterParams: {
                filterOptions: ["contains"],
            },
            cellRenderer: (row) => {
                return RespondentSettingsData.types_of_graph.filter((ele) => ele.value === row.value)?.[0]?.label || row.value.charAt(0).toUpperCase() + row.value.slice(1).toLowerCase() || row.value;
            },
        },
        {
            headerName: "Active/Retired",
            field: "active_retired_label",
            minWidth: 160,

            autoHeight: true,
            filter: true,
            suppressMenu: true,
            floatingFilter: true,
            filterParams: {
                filterOptions: ["contains"],
            },
        },
        {
            headerName: "Last edited by",
            field: "updated_by__username",
            minWidth: 280,
            autoHeight: true,
            filter: true,
            suppressMenu: true,
            floatingFilter: true,
            filterParams: {
                filterOptions: ["contains"],
            },
        },
        {
            headerName: "Action",
            maxWidth: 120,
            cellRenderer: editCellRenderer,
        },
    ];

    const handleUpdateClick = (row) => {
        setSelectedRowToUpdate(row.data);
        setIsUpdateDialogOpen(true);
    };

    const defaultColDef = useMemo(() => {
        return {
            editable: false,
            sortable: true,
            resizable: true,
            cellStyle: {
                fontSize: "14px",
                border: "none",
            },
            minWidth: 200,
        };
    }, []);

    const sortingOrder = useMemo(() => {
        return ["desc", "asc", null];
    }, []);

    const onSelectionChanged = useCallback(() => {
        let _selectedRows = gridRef.current.api.getSelectedRows();
        setSelectedRows(_selectedRows);
    }, []);

    const handleError = useCallback(
        (type, error) => {
            dispatch(showNotification({ severity: type, data: error }));
        },
        [dispatch]
    );

    const fetchData = useCallback(() => {
        if (productCategoryId && selectedStudyType?.value && selectedStudyTypeMethod && selectedStudyTypeMethod.length > 0) {
            try {
                let url = process.env.REACT_APP_SERVER_PATH + `/respondent-settings/all/?archive=False`;
                if (productCategoryId) {
                    url += `?product_category=${productCategoryId}`;
                }
                if (selectedStudyType?.value) {
                    url += `&study_type=${selectedStudyType.value}`;
                }
                if (selectedStudyTypeMethod.length > 0) {
                    const methodIds = selectedStudyTypeMethod.map(m => m.value).join(',');
                    url += `&study_type_method=${methodIds}`;
                }
                setIsLoading(true);
                api.get(url)
                    .then(async (res) => {
                        const _data =
                            res?.data?.map((row) => {
                                return {
                                    ...row,
                                    active_retired_label: row?.active ? "Active" : "Retired",
                                };
                            }) || [];
                        setRowData([..._data]);
                        setIsLoading(false);
                    })
                    .catch(async (error) => {
                        handleError("error", error);
                        setIsLoading(false);
                    });
            } catch (error) {
                handleError("error", error);
                setIsLoading(false);
            }
        }
    }, [productCategoryId, selectedStudyType?.value, selectedStudyTypeMethod, handleError]);

    const fetchUniqueBackendVariableData = useCallback(() => {
        if (productCategoryId && selectedStudyType?.value && selectedStudyTypeMethod && selectedStudyTypeMethod.length > 0) {
            setIsLoading(true);
            let url = process.env.REACT_APP_SERVER_PATH + `/respondent-settings/data-mappings/`;
            if (productCategoryId) {
                url += `?product_category=${productCategoryId}`;
            }
            if (selectedStudyType?.value) {
                url += `&study_type=${selectedStudyType.value}`;
            }
            if (selectedStudyTypeMethod.length > 0) {
                const methodIds = selectedStudyTypeMethod.map(m => m.value).join(',');
                url += `&study_type_method=${methodIds}`;
            }
            try {
                api.get(url)
                    .then(async (res) => {
                        setUniqueBackendVariableList([...res.data]);
                        setIsLoading(false);
                    })
                    .catch(async (error) => {
                        handleError("error", error);
                    });
            } catch (error) {
                handleError("error", error);
            }
        }
    }, [productCategoryId, selectedStudyType?.value, selectedStudyTypeMethod, handleError]);

    useEffect(() => {
        fetchData();
        fetchUniqueBackendVariableData();
    }, [fetchData, fetchUniqueBackendVariableData]);

    useEffect(() => {
        const fetchStudyTypes = async () => {
            try {
                let url = process.env.REACT_APP_SERVER_PATH + `/study-types/all/`;
                if (productCategoryId) {
                    url += `?product_category=${productCategoryId}`;
                }
                await api
                    .get(url)
                    .then((res) => {
                        const options = transformStudyTypeOptions(res.data);
                        setStudyTypeOptions(options);
                    })
                    .catch((error) => {
                        handleError("error", error);
                        setIsLoading(false);
                    });
            } catch (error) {
                handleError("error", error);
                setIsLoading(false);
            }
        };

        fetchStudyTypes();
    }, [productCategoryId, handleError]);

    // Fetch study type methods when study type changes
    useEffect(() => {
        const fetchStudyTypeMethods = async () => {
            setRowData([]);
            setUniqueBackendVariableList([]);
            try {
                let url = process.env.REACT_APP_SERVER_PATH + `/study-type-methods/all/`;
                if (productCategoryId) {
                    url += `?product_category=${productCategoryId}`;
                }
                if (selectedStudyType?.value) {
                    url += `&study_type=${selectedStudyType.value}`;
                }

                await api
                    .get(url)
                    .then((res) => {
                        const options = transformStudyTypeMethodOptions(res.data);
                        setStudyTypeMethodOptions(options);
                    })
                    .catch((error) => {
                        handleError("error", error);
                        setIsLoading(false);
                    });
            } catch (error) {
                handleError("error", error);
                setIsLoading(false);
            }
        };

        fetchStudyTypeMethods();
    }, [selectedStudyType, productCategoryId, handleError]);

    const handleDelete = () => {
        const selectedIds = selectedRows.map((datum) => datum.id);
        const payload = { ids: selectedIds };
        try {
            let url = process.env.REACT_APP_SERVER_PATH + `/respondent-settings/multiple-delete/`;
            api.put(url, payload)
                .then(() => {
                    fetchData();
                    handleConfirmationDialogClose();
                })
                .catch((error) => {
                    handleError("error", error);
                });
        } catch (error) {
            handleError("error", error);
        }
    };

    const handleConfirmationDialogClose = () => {
        setIsDeleteDialogOpen(false);
        setSelectedRows([]);
    };

    const handleChange = (selectedValue, type, actionMeta) => {
        if (type === "studyType") {
            setSelectedStudyType(selectedValue);
            dispatch(loadRespondentSettingsStudyType(selectedValue));

            if (!selectedValue || (selectedValue && selectedStudyTypeMethod[0]?.studyTypeValue !== selectedValue.value)) {
                setSelectedStudyTypeMethod([]);
                dispatch(loadRespondentSettingsStudyTypeMethod([]));
            }
        } else {
            if (actionMeta?.action === "select-option") {
                const selectedTemplate = actionMeta.option.template;

                const methodsForTemplate = studyTypeMethodOptions.filter(
                    (opt) => opt.template === selectedTemplate
                );
                const selectionMap = new Map();

                [...selectedStudyTypeMethod, ...methodsForTemplate].forEach((item) => {
                    selectionMap.set(item.value, item);
                });

                const uniqueSelection = Array.from(selectionMap.values());

                setSelectedStudyTypeMethod(uniqueSelection);
                dispatch(loadRespondentSettingsStudyTypeMethod(uniqueSelection));
            } 
            else if (actionMeta?.action === "remove-value") {
                const templateToRemove = actionMeta.removedValue?.template;
                const newSelection = selectedStudyTypeMethod.filter(
                    (opt) => opt.template !== templateToRemove
                );

                setSelectedStudyTypeMethod(newSelection);
                dispatch(loadRespondentSettingsStudyTypeMethod(newSelection));
            } 
            else if (actionMeta?.action === "clear") {
                setSelectedStudyTypeMethod([]);
                dispatch(loadRespondentSettingsStudyTypeMethod([]));
            }
        }
    };

    return (
        <>
            {isLoading && <Loader />}
            <div
                className="ag-theme-alpine"
                style={{
                    height: "calc(100vh - 250px)",
                    width: "100%",
                    position: "relative",
                }}
            >
                <div
                    style={{
                        display: "flex",
                        flexDirection: "row",
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                        marginBottom: "30px",
                        padding: "0 15px",
                        gap: "15px",
                        flexWrap: "wrap",
                    }}
                >
                    <div
                        style={{
                            display: "flex",
                            alignItems: "flex-start",
                            gap: "10px",
                            flexWrap: "wrap",
                            flex: 1,
                            minWidth: 0,
                        }}
                    >
                        <div style={{ width: "200px", minWidth: "150px" }}>
                            <Select
                                options={studyTypeOptions}
                                value={selectedStudyType}
                                onChange={(newValue, actionMeta) => handleChange(newValue, "studyType", actionMeta)}
                                formatOptionLabel={formatStudyTypeLabel}
                                styles={ReactSelectStyles}
                                placeholder="Study Type"
                                isClearable
                            />
                        </div>

                        <div style={{ width: "300px", minWidth: "400px"}}>
                            <Select
                                isMulti
                                closeMenuOnSelect={false}
                                hideSelectedOptions={false}
                                options={optionsWithInfoMessage}
                                styles={customMethodReactSelectStyles}
                                value={selectedStudyTypeMethod}
                                onChange={(newValue, actionMeta) => handleChange(newValue, "studyTypeMethod", actionMeta)}
                                formatOptionLabel={formatMethodLabel}
                                placeholder="Study Type Method"
                                isClearable
                            />
                        </div>
                    </div>
                    <div
                        style={{
                            display: "flex",
                            justifyContent: "end",
                            marginBottom: 10,
                        }}
                    >
                        <Button
                            size="small"
                            className="custom-btn me-2"
                            variant="outlined"
                            startIcon={<ControlPointOutlinedIcon />}
                            style={
                                !selectedStudyType || !selectedStudyTypeMethod || selectedStudyTypeMethod.length === 0
                                    ? {
                                          color: "rgba(0, 0, 0, 0.26)",
                                          borderColor: "rgba(0, 0, 0, 0.12)",
                                      }
                                    : {
                                          color: "#005eef",
                                          borderColor: "#005eef",
                                      }
                            }
                            onClick={() => setIsAddDialogOpen(true)}
                            disabled={!selectedStudyType || !selectedStudyTypeMethod || selectedStudyTypeMethod.length === 0}
                        >
                            Add
                        </Button>
                        <Button
                            size="small"
                            className="custom-btn"
                            variant="outlined"
                            startIcon={<DeleteOutlinedIcon />}
                            style={
                                selectedRows.length === 0
                                    ? {
                                          color: "rgba(0, 0, 0, 0.26)",
                                          borderColor: "rgba(0, 0, 0, 0.12)",
                                      }
                                    : {
                                          color: "red",
                                          borderColor: "red",
                                      }
                            }
                            onClick={() => setIsDeleteDialogOpen(true)}
                            disabled={selectedRows.length === 0}
                        >
                            Delete
                        </Button>
                    </div>
                </div>
                <style>
                    {`
                        .ag-header-group-cell-label {
                            justify-content: center !important;
                        }
                        .ag-header-cell-comp-wrapper {
                            justify-content: center !important;
                        }
                    `}
                </style>
                <AgGridReact
                    rowStyle={{
                        background: "white",
                    }}
                    ref={gridRef}
                    rowSelection="multiple"
                    rowMultiSelectWithClick={true}
                    defaultColDef={defaultColDef}
                    rowData={rowData}
                    columnDefs={columnDefs}
                    onGridReady={(params) => params.id}
                    onSelectionChanged={onSelectionChanged}
                    // sideBar={false}
                    rowHeight={35}
                    headerHeight={40}
                    floatingFiltersHeight={40}
                    suppressDragLeaveHidesColumns={true}
                    sortingOrder={sortingOrder}
                    suppressRowClickSelection={true}
                ></AgGridReact>
            </div>
            <div className="d-flex">
                <span
                    style={{
                        fontSize: 12,
                        color: "#495057",
                        marginRight: 20,
                        fontWeight: 600,
                    }}
                >
                    Selected rows: {selectedRows.length}
                </span>
                <span
                    style={{
                        fontSize: 12,
                        color: "#495057",
                        fontWeight: 600,
                    }}
                >
                    Total rows: {rowData.length}
                </span>
            </div>
            {isAddDialogOpen && (
                <SettingsAddUpdate
                    title="Add New Variable for Respondent Dashboard"
                    type="add"
                    isOpen={isAddDialogOpen}
                    setIsAddDialogOpen={setIsAddDialogOpen}
                    fetchData={fetchData}
                    uniqueBackendVariableList={uniqueBackendVariableList}
                    studyType={selectedStudyType}
                    studyTypeMethod={selectedStudyTypeMethod}
                    productCategoryLabel={productCategoryLabel}
                />
            )}
            {isUpdateDialogOpen && (
                <SettingsAddUpdate
                    title="Update Variable for Respondent Dashboard"
                    type="update"
                    selectedRows={selectedRowToUpdate}
                    isOpen={isUpdateDialogOpen}
                    setIsUpdateDialogOpen={setIsUpdateDialogOpen}
                    fetchData={fetchData}
                    uniqueBackendVariableList={uniqueBackendVariableList}
                    productCategoryLabel={productCategoryLabel}
                />
            )}
            <div></div>
            {isDeleteDialogOpen && (
                <Dialog
                    sx={{
                        "& .MuiDialog-paper": {
                            maxWidth: "660px",
                            width: "100%",
                        },
                    }}
                    open={isDeleteDialogOpen}
                    aria-describedby="alert-dialog-slide-description"
                >
                    <div
                        style={{
                            padding: "10px 20px",
                            color: "rgb(92, 92, 92)",
                        }}
                    >
                        <div>
                            <p
                                style={{
                                    fontWeight: 600,
                                }}
                            >
                                Are you sure you want to delete the following settings?
                            </p>
                        </div>
                        <div>
                            <table
                                style={{
                                    fontSize: "14px",
                                    width: "100%",
                                    borderCollapse: "collapse",
                                    fontFamily: "Arial, sans-serif",
                                }}
                            >
                                <thead>
                                    <tr
                                        style={{
                                            backgroundColor: "#F2F2F2",
                                        }}
                                    >
                                        <th
                                            style={{
                                                border: "1px solid #dddddd",
                                                textAlign: "left",
                                                padding: "8px",
                                            }}
                                        >
                                            Sl No.
                                        </th>
                                        <th
                                            style={{
                                                border: "1px solid #dddddd",
                                                textAlign: "left",
                                                padding: "8px",
                                            }}
                                        >
                                            Backend Variable
                                        </th>
                                        <th
                                            style={{
                                                border: "1px solid #dddddd",
                                                textAlign: "left",
                                                padding: "8px",
                                            }}
                                        >
                                            Display Label
                                        </th>
                                        <th
                                            style={{
                                                border: "1px solid #dddddd",
                                                textAlign: "left",
                                                padding: "8px",
                                            }}
                                        >
                                            Variable Category
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {selectedRows.map((ele, i) => (
                                        <tr key={ele.backend_variable}>
                                            <td
                                                style={{
                                                    border: "1px solid #dddddd",
                                                    padding: "8px",
                                                }}
                                            >
                                                {i + 1}
                                            </td>
                                            <td
                                                style={{
                                                    border: "1px solid #dddddd",
                                                    padding: "8px",
                                                }}
                                            >
                                                {ele.backend_variable}
                                            </td>
                                            <td
                                                style={{
                                                    border: "1px solid #dddddd",
                                                    padding: "8px",
                                                }}
                                            >
                                                {ele.display_label}
                                            </td>
                                            <td
                                                style={{
                                                    border: "1px solid #dddddd",
                                                    padding: "8px",
                                                }}
                                            >
                                                {ele.variable_category}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    <DialogActions>
                        <Button
                            className="me-2 custom-btn liner-light-blue-background"
                            size="small"
                            variant="contained"
                            color="error"
                            onClick={handleConfirmationDialogClose}
                        >
                            Disagree
                        </Button>
                        <Button
                            className="me-2 custom-btn liner-light-blue-background"
                            size="small"
                            variant="contained"
                            onClick={handleDelete}
                        >
                            Agree
                        </Button>
                    </DialogActions>
                </Dialog>
            )}
        </>
    );
}

export default Settings;
